import { apiService } from './apiService';
import { AxiosResponse } from 'axios';

/**
 * Pagination Service
 * Provides automatic pagination handling for API endpoints that return paginated data.
 * Acts as a drop-in replacement for apiService.get with automatic page fetching.
 */

interface PaginatedResponse {
  member?: any[];
  'hydra:member'?: any[];
  totalItems?: number;
  'hydra:totalItems'?: number;
  [key: string]: any;
}

/**
 * Fetches all pages from a paginated API endpoint automatically
 * @param url - The API endpoint URL (without pagination params)
 * @param headers - Optional headers to pass to the request
 * @param itemsPerPage - Number of items per page (default: 30)
 * @returns Promise<AxiosResponse> with all items combined in the member array
 */
export const getPaginated = async <T = any>(
  url: string,
  headers?: any,
  itemsPerPage: number = 30
): Promise<AxiosResponse<T>> => {
  // Parse existing query params from URL
  const urlObj = new URL(url, 'http://dummy.com'); // dummy base for relative URLs
  const existingParams = new URLSearchParams(urlObj.search);
  const baseUrl = url.split('?')[0];
  
  let allItems: any[] = [];
  const page = 1;
  let totalItems = 0;
  let firstResponse: AxiosResponse<any> | null = null;

  // Fetch first page to get totalItems
  const firstPageParams = new URLSearchParams(existingParams);
  firstPageParams.set('page', page.toString());
  firstPageParams.set('itemsPerPage', itemsPerPage.toString());
  
  const firstPageUrl = `${baseUrl}?${firstPageParams.toString()}`;
  firstResponse = await apiService.get(firstPageUrl, headers);
  
  const firstPageData: PaginatedResponse = firstResponse.data;
  
  // Extract items from response (support both 'member' and 'hydra:member')
  const firstPageItems = firstPageData.member || firstPageData['hydra:member'] || [];
  allItems = [...firstPageItems];
  
  // Get total items count (support both 'totalItems' and 'hydra:totalItems')
  totalItems = firstPageData.totalItems || firstPageData['hydra:totalItems'] || firstPageItems.length;
  
  // Calculate total pages needed
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Fetch remaining pages in parallel if needed
  if (totalPages > 1 && allItems.length < totalItems) {
    const pagePromises: Promise<AxiosResponse<any>>[] = [];
    
    for (let p = 2; p <= totalPages; p++) {
      const pageParams = new URLSearchParams(existingParams);
      pageParams.set('page', p.toString());
      pageParams.set('itemsPerPage', itemsPerPage.toString());
      
      const pageUrl = `${baseUrl}?${pageParams.toString()}`;
      pagePromises.push(apiService.get(pageUrl, headers));
    }
    
    const remainingResponses = await Promise.all(pagePromises);
    
    remainingResponses.forEach((response) => {
      const pageData: PaginatedResponse = response.data;
      const pageItems = pageData.member || pageData['hydra:member'] || [];
      allItems = [...allItems, ...pageItems];
    });
  }
  
  // Construct the final response matching the original format
  const finalResponse: AxiosResponse<T> = {
    ...firstResponse,
    data: {
      ...firstPageData,
      member: allItems,
      'hydra:member': allItems,
      totalItems: totalItems,
      'hydra:totalItems': totalItems,
    } as T,
  };
  
  return finalResponse;
};

/**
 * Service object that mimics apiService interface but with automatic pagination
 */
export const paginationService = {
  /**
   * GET request with automatic pagination
   * Fetches all pages and combines results automatically
   */
  get: getPaginated,
  
  /**
   * Convenience method: Get with response handler
   * Automatically handles pagination and applies response handler
   */
  getWithHandler: async <T = any>(
    url: string,
    headers?: any,
    itemsPerPage: number = 30
  ): Promise<T> => {
    const response = await getPaginated<T>(url, headers, itemsPerPage);
    return apiService.responseHandler(response);
  },
};
